import SEO from './../components/SEO';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Settings, CheckCircle, AlertCircle, X, RefreshCw } from 'lucide-react';
import { getSettings, updateSettings } from '../services/api';

// ── Toast ──────────────────────────────────────────────────────────────────────
function Toast({ toast, onClose }) {
  if (!toast) return null;
  const isSuccess = toast.type === 'success';
  return (
    <div className={`fixed bottom-4 right-4 z-50 flex items-start gap-3 px-4 py-3 rounded-xl shadow-lg border max-w-sm ${
      isSuccess ? 'bg-green-50 dark:bg-green-950/50 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
                : 'bg-red-50 dark:bg-red-950/50 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
    }`}>
      {isSuccess ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
      <p className="text-sm">{toast.message}</p>
      <button onClick={onClose} className="ml-auto flex-shrink-0 opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
    </div>
  );
}

const THEMES = [
  { id: 'classic', label: 'Classic',  desc: 'Color-coded by invoice type',    preview: ['#2563eb', '#7c3aed', '#059669'] },
  { id: 'dark',    label: 'Dark',     desc: 'Dark background with light text', preview: ['#111827', '#1f2937', '#374151'] },
  { id: 'minimal', label: 'Minimal',  desc: 'Clean black & white',             preview: ['#1f2937', '#6b7280', '#e5e7eb'] },
];

const PADDINGS = [
  { v: 3, example: '001' },
  { v: 4, example: '0001' },
  { v: 5, example: '00001' },
  { v: 6, example: '000001' },
];

export default function SettingsPage() {
  const [form, setForm]       = useState({ invoice_number_prefix: '', invoice_number_padding: 6, pdf_theme: 'classic', pdf_primary_color: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState(null);

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }

  useEffect(() => {
    getSettings()
      .then(s => setForm({
        invoice_number_prefix:  s.invoice_number_prefix  ?? '',
        invoice_number_padding: s.invoice_number_padding ?? 6,
        pdf_theme:              s.pdf_theme              ?? 'classic',
        pdf_primary_color:      s.pdf_primary_color      ?? '',
      }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const previewNumber = (() => {
    const prefix  = form.invoice_number_prefix.trim();
    const padding = Math.max(3, Math.min(6, Number(form.invoice_number_padding) || 6));
    if (prefix) return `${prefix}-${'1'.padStart(padding, '0')}`;
    return `INV-${'1'.padStart(6, '0')}`;
  })();

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        invoice_number_prefix:  form.invoice_number_prefix.trim() || null,
        invoice_number_padding: Number(form.invoice_number_padding),
        pdf_theme:              form.pdf_theme,
        pdf_primary_color:      form.pdf_primary_color.trim() || null,
      };
      await updateSettings(payload);
      showToast('Settings saved successfully.');
    } catch (err) {
      showToast(err.message || 'Failed to save settings.', 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO title="Settings | Invilic" description="Manage your Invilic account settings." canonical="https://www.invilic.com/settings" noindex={true} />
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-4 sm:px-6 flex items-center gap-4">
          <Link to="/" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-400" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Settings</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:px-6 space-y-6">

        {/* ── Invoice Number Format ─────────────────────────────── */}
        <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-5">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Invoice Number Format</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Customise the prefix and digit count for new invoice numbers.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Custom Prefix <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                maxLength={30}
                placeholder="e.g. ACME-2025"
                value={form.invoice_number_prefix}
                onChange={e => setForm(f => ({ ...f, invoice_number_prefix: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">Leave blank to use the default (INV, GST-INV, etc.)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Digit Padding</label>
              <select
                value={form.invoice_number_padding}
                onChange={e => setForm(f => ({ ...f, invoice_number_padding: Number(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PADDINGS.map(p => (
                  <option key={p.v} value={p.v}>{p.v} digits ({p.example})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
            <span className="text-sm text-gray-600 dark:text-gray-400">Preview:</span>
            <span className="font-mono font-semibold text-blue-700 dark:text-blue-400 text-sm">{previewNumber}</span>
          </div>
        </section>

        {/* ── PDF Theme ────────────────────────────────────────── */}
        <section className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 space-y-5">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">PDF Theme</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Choose how your generated invoice PDFs look.</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            {THEMES.map(t => (
              <button
                key={t.id}
                onClick={() => setForm(f => ({ ...f, pdf_theme: t.id }))}
                className={`text-left p-4 rounded-xl border-2 transition-colors ${
                  form.pdf_theme === t.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex gap-1.5 mb-3">
                  {t.preview.map((c, i) => (
                    <div key={i} className="w-6 h-6 rounded-full border border-white/20 shadow" style={{ backgroundColor: c }} />
                  ))}
                </div>
                <p className="font-medium text-sm text-gray-900 dark:text-white">{t.label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t.desc}</p>
              </button>
            ))}
          </div>
        </section>

        {/* ── Save ─────────────────────────────────────────────── */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </main>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
